/**
 * Phone normalization for admin forms. Turns whatever an operator types —
 * `0244123456`, `244123456`, `233…`, `00233…`, `+233 24 412 3456` — into a
 * single canonical E.164 value (`+233244123456`).
 *
 * Mirrors ff-ui's `client/utils/phone.ts`, minus the region hook: ff-admin has
 * no CountryContext, so everything defaults to Ghana. Keep the two in sync.
 */
import {
  parsePhoneNumberFromString,
  isSupportedCountry,
  AsYouType,
  type CountryCode,
} from 'libphonenumber-js';

export interface PhoneRegion {
  /** ISO-3166 alpha-2 (e.g. "GH"). Drives national-prefix / format rules. */
  country?: string | null;
  /** Dial-code fallback (e.g. "+233") for when the country isn't a known ISO code. */
  dialCode?: string | null;
}

/** Ghana default — matches ff-api rest-of-world behaviour until full i18n lands. */
export const DEFAULT_PHONE_REGION: Required<PhoneRegion> = {
  country: 'GH',
  dialCode: '+233',
};

function resolveCountry(country?: string | null): CountryCode | undefined {
  if (!country) return undefined;
  const upper = country.toUpperCase();
  return isSupportedCountry(upper) ? (upper as CountryCode) : undefined;
}

/**
 * Normalize raw input to E.164 (`+233244123456`), or `""` when empty.
 * Falls back to a best-effort dial-code prefix when the input can't be parsed
 * so we never emit a bare national number.
 */
export function normalizePhoneToE164(
  raw: string | null | undefined,
  region: PhoneRegion = {},
): string {
  const input = (raw ?? '').trim();
  if (!input) return '';

  const country =
    resolveCountry(region.country) ?? resolveCountry(DEFAULT_PHONE_REGION.country);

  const parsed = parsePhoneNumberFromString(input, country);
  if (parsed) return parsed.number;

  const dialCode = region.dialCode || DEFAULT_PHONE_REGION.dialCode;
  const cleaned = input.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  const national = cleaned.replace(/^0+/, '');
  return national ? `${dialCode}${national}` : '';
}

/** True when the input normalizes to a valid phone number for the region. */
export function isValidPhone(
  raw: string | null | undefined,
  region: PhoneRegion = {},
): boolean {
  const e164 = normalizePhoneToE164(raw, region);
  if (!e164) return false;
  const parsed = parsePhoneNumberFromString(e164);
  return !!parsed && parsed.isValid();
}

/**
 * Pretty international form for display (`+233 24 412 3456`). Falls back to the
 * plain E.164 when the number isn't yet valid. Always submit
 * `normalizePhoneToE164` to the backend, never this.
 */
export function formatPhoneForDisplay(
  raw: string | null | undefined,
  region: PhoneRegion = {},
): string {
  const e164 = normalizePhoneToE164(raw, region);
  if (!e164) return '';
  const parsed = parsePhoneNumberFromString(e164);
  return parsed ? parsed.formatInternational() : e164;
}

/** As-you-type formatting for a live input, keyed to the region's country. */
export function formatPhoneAsYouType(raw: string, region: PhoneRegion = {}): string {
  const country =
    resolveCountry(region.country) ?? resolveCountry(DEFAULT_PHONE_REGION.country);
  return new AsYouType(country).input(raw ?? '');
}

/**
 * Split a comma / semicolon / newline separated list into E.164 values, keeping
 * the ones that don't parse so the caller can report them rather than silently
 * dropping a recipient.
 */
export function normalizePhoneList(
  raw: string,
  region: PhoneRegion = {},
): { valid: string[]; invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();

  for (const entry of raw.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean)) {
    if (!isValidPhone(entry, region)) {
      invalid.push(entry);
      continue;
    }
    const e164 = normalizePhoneToE164(entry, region);
    if (seen.has(e164)) continue;
    seen.add(e164);
    valid.push(e164);
  }

  return { valid, invalid };
}
